/** Client mirror of server/utils/countReviewFlags.js */

export const FLAG_PRIORITY_ZERO = 'priority_zero';
export const FLAG_HIGH_VALUE = 'high_value';
export const FLAG_GLOBAL_VALUE_THRESHOLD = 'global_value_threshold';

function parseDecimal(val) {
  if (val === undefined || val === null || val === '') return null;
  const n = parseFloat(String(val).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

/** Blank/null reviewed qty → 0 for flags and comparisons (matches server coerceReviewedCountQuantity). */
export function coerceReviewedCountQuantity(val) {
  if (val === undefined) return { skip: true };
  if (val === null) return { value: 0 };
  if (typeof val === 'string' && val.trim() === '') return { value: 0 };
  const n = parseDecimal(val);
  if (n === null) return { invalid: true };
  return { value: n };
}

export function reviewedQtyOrZero(val) {
  const c = coerceReviewedCountQuantity(val === undefined ? null : val);
  if (c.skip || c.invalid) return 0;
  return c.value;
}

function parseGlobalInventoryValueLimit(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseFloat(String(raw));
  return Number.isNaN(n) ? null : n;
}

function parseThresholdValue(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseFloat(String(raw));
  return Number.isNaN(n) ? null : n;
}

export function normalizeClusterKey(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim().replace(/\s+/g, ' ');
  return s === '' ? null : s.toLowerCase();
}

export function parseThresholdMap(raw) {
  if (raw === null || raw === undefined || raw === '') return {};
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch (_) {
      return {};
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = normalizeClusterKey(k);
    if (!key) continue;
    const n = parseThresholdValue(v);
    if (n != null) out[key] = n;
  }
  return out;
}

export function parseSiteCluster(site) {
  if (!site) return { key: null, label: null };
  let extras = site.extras;
  if (typeof extras === 'string') {
    try {
      extras = JSON.parse(extras);
    } catch (_) {
      extras = {};
    }
  }
  if (!extras || typeof extras !== 'object') extras = {};
  const fromExtras = extras.cluster != null && String(extras.cluster).trim() !== '' ? extras.cluster : null;
  const fromCol = site.cluster != null && String(site.cluster).trim() !== '' ? site.cluster : null;
  const raw = fromExtras != null ? fromExtras : fromCol;
  if (raw == null || String(raw).trim() === '') return { key: null, label: null };
  const label = String(raw).trim().replace(/\s+/g, ' ');
  return { key: normalizeClusterKey(label), label };
}

function parseItemThresholdsByCluster(inventoryItem) {
  if (!inventoryItem || inventoryItem.valueThresholdsByCluster == null) return {};
  return parseThresholdMap(inventoryItem.valueThresholdsByCluster);
}

function parseItemValueThreshold(inventoryItem) {
  if (
    !inventoryItem ||
    inventoryItem.valueThreshold == null ||
    inventoryItem.valueThreshold === ''
  ) {
    return null;
  }
  return parseThresholdValue(inventoryItem.valueThreshold);
}

export function normalizeThresholdContext(input) {
  if (input == null || typeof input !== 'object') {
    return {
      siteClusterKey: null,
      siteClusterLabel: null,
      globalInventoryValueLimitsByCluster: {},
      globalInventoryValueLimit: input
    };
  }
  const byCluster =
    input.globalInventoryValueLimitsByCluster != null
      ? typeof input.globalInventoryValueLimitsByCluster === 'object' &&
        !Array.isArray(input.globalInventoryValueLimitsByCluster)
        ? input.globalInventoryValueLimitsByCluster
        : parseThresholdMap(input.globalInventoryValueLimitsByCluster)
      : {};
  return {
    siteClusterKey: input.siteClusterKey || null,
    siteClusterLabel: input.siteClusterLabel || null,
    globalInventoryValueLimitsByCluster: byCluster,
    globalInventoryValueLimit: input.globalInventoryValueLimit
  };
}

export function resolveEffectiveValueThreshold(inventoryItem, thresholdContext) {
  const ctx = normalizeThresholdContext(thresholdContext);
  const siteClusterKey = ctx.siteClusterKey;
  const globalByCluster = ctx.globalInventoryValueLimitsByCluster || {};
  const legacyGlobal = parseGlobalInventoryValueLimit(ctx.globalInventoryValueLimit);

  if (siteClusterKey) {
    const itemMap = parseItemThresholdsByCluster(inventoryItem);
    const clusterItem = itemMap[siteClusterKey];
    if (clusterItem != null) {
      return {
        threshold: clusterItem,
        source: 'item',
        thresholdTier: 'item_cluster',
        clusterKey: siteClusterKey
      };
    }
  }

  const legacyItem = parseItemValueThreshold(inventoryItem);
  if (legacyItem != null) {
    return {
      threshold: legacyItem,
      source: 'item',
      thresholdTier: 'item_legacy',
      clusterKey: siteClusterKey
    };
  }

  if (siteClusterKey) {
    const clusterGlobal = globalByCluster[siteClusterKey];
    if (clusterGlobal != null) {
      return {
        threshold: clusterGlobal,
        source: 'global',
        thresholdTier: 'global_cluster',
        clusterKey: siteClusterKey
      };
    }
  }

  if (legacyGlobal != null) {
    return {
      threshold: legacyGlobal,
      source: 'global',
      thresholdTier: 'global_legacy',
      clusterKey: siteClusterKey
    };
  }

  return {
    threshold: null,
    source: null,
    thresholdTier: null,
    clusterKey: siteClusterKey
  };
}

function isPriorityItem(item) {
  if (!item) return false;
  return (
    item.priority === true ||
    item.priority === 'true' ||
    item.priority === 1 ||
    item.priority === '1' ||
    Boolean(item.priority)
  );
}

function lineHasPositiveQty(countedQuantity, countedQuantity2, unit2) {
  const q1 = parseDecimal(countedQuantity);
  const q2 = parseDecimal(countedQuantity2);
  const n1 = q1 !== null ? q1 : 0;
  const n2 = q2 !== null ? q2 : 0;
  const hasUnit2 = unit2 && String(unit2).trim() !== '';
  if (hasUnit2) return n1 !== 0 || n2 !== 0;
  return n1 !== 0;
}

function computeLineTotal(value1, value2, countedQuantity, countedQuantity2) {
  const v1 = parseFloat(value1 != null ? value1 : 0) || 0;
  const v2 = parseFloat(value2 != null ? value2 : 0) || 0;
  const q1 = parseDecimal(countedQuantity);
  const q2 = parseDecimal(countedQuantity2);
  const n1 = q1 !== null ? q1 : 0;
  const n2 = q2 !== null ? q2 : 0;
  return v1 * n1 + v2 * n2;
}

export function buildThresholdContextForBatch(batch, globalInventoryValueLimit, globalInventoryValueLimitsByCluster) {
  return {
    siteClusterKey: batch?.siteClusterKey || null,
    siteClusterLabel: batch?.siteClusterLabel || null,
    globalInventoryValueLimit,
    globalInventoryValueLimitsByCluster: globalInventoryValueLimitsByCluster || {}
  };
}

export function computeReviewFlags({
  inventoryItem,
  wasCounted,
  countedQuantity,
  countedQuantity2,
  thresholdContext,
  siteClusterKey,
  globalInventoryValueLimitsByCluster,
  globalInventoryValueLimit
}) {
  const ctx =
    thresholdContext != null
      ? normalizeThresholdContext(thresholdContext)
      : normalizeThresholdContext({
          siteClusterKey,
          globalInventoryValueLimitsByCluster,
          globalInventoryValueLimit
        });

  const flagTypes = [];
  const priority = isPriorityItem(inventoryItem);
  const hasPositive = lineHasPositiveQty(
    countedQuantity,
    countedQuantity2,
    inventoryItem ? inventoryItem.unit2 : ''
  );
  if (priority && (!wasCounted || !hasPositive)) {
    flagTypes.push(FLAG_PRIORITY_ZERO);
  }
  const total = computeLineTotal(
    inventoryItem ? inventoryItem.value1 : 0,
    inventoryItem ? inventoryItem.value2 : 0,
    countedQuantity,
    countedQuantity2
  );
  const resolved = resolveEffectiveValueThreshold(inventoryItem, ctx);
  if (resolved.threshold != null && total > resolved.threshold) {
    if (resolved.source === 'item') {
      flagTypes.push(FLAG_HIGH_VALUE);
    } else if (resolved.source === 'global') {
      flagTypes.push(FLAG_GLOBAL_VALUE_THRESHOLD);
    }
  }
  return {
    flagTypes,
    requiresManagerAck: flagTypes.length > 0,
    appliedValueThreshold: resolved.threshold,
    appliedValueThresholdSource: resolved.thresholdTier,
    appliedValueThresholdClusterKey: resolved.clusterKey
  };
}

export function isValidAcknowledgementComment(comment) {
  return typeof comment === 'string' && comment.trim().length > 0;
}

export function effectiveRowQty(item, editedCounts) {
  const ec = editedCounts[item.itemId];
  if (!ec) {
    return {
      wasCounted: item.wasCounted,
      countedQuantity: item.countedQuantity,
      countedQuantity2: item.countedQuantity2
    };
  }
  const q1 = ec.countedQuantity !== undefined ? reviewedQtyOrZero(ec.countedQuantity) : item.countedQuantity;
  const q2 = ec.countedQuantity2 !== undefined ? reviewedQtyOrZero(ec.countedQuantity2) : item.countedQuantity2;
  const hasPositive = lineHasPositiveQty(q1, q2, item.unit2);
  return {
    wasCounted: item.wasCounted || hasPositive,
    countedQuantity: q1,
    countedQuantity2: q2
  };
}

export function rowRequiresManagerAck(item, editedCounts, thresholdContext) {
  const eff = effectiveRowQty(item, editedCounts);
  return computeReviewFlags({
    inventoryItem: item,
    wasCounted: eff.wasCounted,
    countedQuantity: eff.countedQuantity,
    countedQuantity2: eff.countedQuantity2,
    thresholdContext
  });
}

export function flagTypeLabels(flagTypes) {
  const labels = [];
  if ((flagTypes || []).includes(FLAG_PRIORITY_ZERO)) labels.push('Priority zero/no entry');
  if ((flagTypes || []).includes(FLAG_HIGH_VALUE)) labels.push('High value (item threshold)');
  if ((flagTypes || []).includes(FLAG_GLOBAL_VALUE_THRESHOLD)) labels.push('Global value threshold');
  return labels;
}

/** Threshold context for site count submission from selected site + tenant global settings. */
export function buildThresholdContextForSite(site, globalInventoryValueLimit, globalInventoryValueLimitsByCluster) {
  const { key, label } = parseSiteCluster(site);
  return {
    siteClusterKey: key,
    siteClusterLabel: label,
    globalInventoryValueLimit,
    globalInventoryValueLimitsByCluster: globalInventoryValueLimitsByCluster || {}
  };
}

/** Site submit review requires a note when any review flag is present (matches manager flag types). */
export function submissionNoteRequiredFromFlags(flagTypes) {
  const set = new Set(flagTypes || []);
  return (
    set.has(FLAG_PRIORITY_ZERO) ||
    set.has(FLAG_HIGH_VALUE) ||
    set.has(FLAG_GLOBAL_VALUE_THRESHOLD)
  );
}
